import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import {
  FRIENDSHIP_GOALS,
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  POLITICS_OPTIONS,
  RELIGION_OPTIONS,
} from "@/constants/options";
import { AgeRangeSlider } from "@/components/age-range-slider";
import { NearbyRadiusSlider } from "@/components/nearby-radius-slider";
import { Avatar } from "@/components/ui/avatar";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { KeyboardScrollView } from "@/components/ui/keyboard-view";
import { notify } from "@/lib/dialog";
import {
  AGE_RANGE_MIN,
  AGE_RANGE_SLIDER_MAX,
  ANY_AGE_RANGE_LABEL,
  formatAgeRange,
  parseAgeRangeForMatch,
} from "@/lib/age-range";
import { clampNearbyRadiusM } from "@/lib/nearby-radius";
import i18n, { type TranslationKey } from "@/i18n";
import {
  MAX_DISPLAY_NAME_LENGTH,
  collapseDisplayName,
  generateDisplayName,
  validateDisplayName,
} from "@/lib/display-name";
import { useAuthStore } from "@/state/auth-store";
import { useSessionStore } from "@/state/session-store";
import { useAppTheme, type AppColors } from "@/theme/app-theme";
import type { VisibilityPreferences } from "@/types";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

const MAX_INTERESTS = 8;
const MAX_BIO_LENGTH = 180;
const MAX_HEADLINE_LENGTH = 50;
const MAX_CUSTOM_OPTIONS = 5;
const MAX_LANGUAGES = 8;
const OTHER_GOAL_VALUE = "other";
const CUSTOM_LANGUAGE_VALUE = "Other";
const ALL_GENDERS_VALUE = "__all_genders";
const OPTIONAL_VALUE = "__optional";

type ProfileDraft = {
  displayName: string;
  dateOfBirth: string;
  headline: string;
  bio: string;
  interests: string[];
  lookingFor: string[];
  languages: string[];
  religion: string;
  politics: string;
  gender: string;
};

type ChipOption = {
  label: string;
  value: string;
  /**
   * Optional second line shown under the label in the picker sheet. Only the
   * nearby-range options use it — everywhere else a bare label is enough, and
   * the chip summary never shows it.
   */
  description?: string;
};

function translatedProfileOption(
  key: TranslationKey,
  value: string,
): ChipOption {
  return {
    get label() {
      return i18n.t(key);
    },
    value,
  };
}

const OPTIONAL_OPTION: ChipOption = {
  get label() {
    return i18n.t("profile.edit.optional");
  },
  value: OPTIONAL_VALUE,
};

// Own gender (single select). "Custom" needs a free-text flow we don't build
// here, so it's excluded from the picker.
const OWN_GENDER_OPTIONS: ChipOption[] = GENDER_OPTIONS.filter(
  (option) => option.value !== "custom",
);

const OPTIONAL_OWN_GENDER_OPTIONS: ChipOption[] = [
  OPTIONAL_OPTION,
  ...OWN_GENDER_OPTIONS,
];

// Genders a user can choose to be visible to (audience). "Prefer not to say"
// and "Custom" aren't meaningful as an audience gate.
const AUDIENCE_GENDER_OPTIONS: ChipOption[] = GENDER_OPTIONS.filter(
  (option) => option.value !== "custom" && option.value !== "prefer-not-to-say",
);

const AUDIENCE_GENDER_FILTER_OPTIONS: ChipOption[] = [
  {
    get label() {
      return i18n.t("profile.edit.all");
    },
    value: ALL_GENDERS_VALUE,
  },
  ...AUDIENCE_GENDER_OPTIONS,
];

const LANGUAGE_FILTER_OPTIONS: ChipOption[] = [
  OPTIONAL_OPTION,
  ...LANGUAGE_OPTIONS,
];

const OPTIONAL_RELIGION_OPTIONS: ChipOption[] = [
  OPTIONAL_OPTION,
  ...RELIGION_OPTIONS,
];

const OPTIONAL_POLITICS_OPTIONS: ChipOption[] = [
  OPTIONAL_OPTION,
  ...POLITICS_OPTIONS,
];

/**
 * The server stores the audience age preference as numbers
 * (`visibilityPreferences.ageMin`/`ageMax`, null = unbounded); the shared
 * slider speaks label strings ("26-40", "50+", null = any). This is
 * `parseAgeRangeForMatch`'s inverse over every value the slider can produce,
 * so reopening the editor never moves a saved range — unlike the age-band
 * mapping this replaces, which snapped a stored 27 out to its band's 25.
 */
function visibilityRangeToLabel(
  ageMin: number | null | undefined,
  ageMax: number | null | undefined,
): string | null {
  if (ageMin == null && ageMax == null) return null;

  const label = formatAgeRange({
    min: ageMin ?? AGE_RANGE_MIN,
    max: ageMax == null ? AGE_RANGE_SLIDER_MAX : Math.min(ageMax, AGE_RANGE_SLIDER_MAX),
  });
  return label === ANY_AGE_RANGE_LABEL ? null : label;
}

const LOOKING_FOR_OPTIONS: ChipOption[] = FRIENDSHIP_GOALS;

const INTEREST_OPTIONS: ChipOption[] = [
  translatedProfileOption("profile.interests.coffee", "coffee"),
  translatedProfileOption("profile.interests.food", "food"),
  translatedProfileOption("profile.interests.music", "music"),
  translatedProfileOption("profile.interests.movies", "movies"),
  translatedProfileOption("profile.interests.books", "books"),
  translatedProfileOption("profile.interests.gaming", "gaming"),
  translatedProfileOption("profile.interests.fitness", "fitness"),
  translatedProfileOption("profile.interests.running", "running"),
  translatedProfileOption("profile.interests.hiking", "hiking"),
  translatedProfileOption("profile.interests.travel", "travel"),
  translatedProfileOption("profile.interests.art", "art"),
  translatedProfileOption("profile.interests.photography", "photography"),
  translatedProfileOption("profile.interests.technology", "technology"),
  translatedProfileOption("profile.interests.startups", "startups"),
  translatedProfileOption("profile.interests.fashion", "fashion"),
  translatedProfileOption("profile.interests.cooking", "cooking"),
  translatedProfileOption("profile.interests.dancing", "dancing"),
  translatedProfileOption("profile.interests.sports", "sports"),
  translatedProfileOption("profile.interests.volunteering", "volunteering"),
  translatedProfileOption("profile.interests.languages", "languages"),
  translatedProfileOption("profile.interests.other", OTHER_GOAL_VALUE),
];

function cleanStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string");
}

function splitOptionValues(value: unknown, options: ChipOption[]) {
  const optionValues = new Set(options.map((option) => option.value));
  const selected: string[] = [];
  const custom: string[] = [];

  cleanStringArray(value).forEach((goal) => {
    if (optionValues.has(goal)) {
      selected.push(goal);
    } else {
      custom.push(goal);
    }
  });

  return {
    selected: custom.length ? [...selected, OTHER_GOAL_VALUE] : selected,
    customValues: custom.slice(0, MAX_CUSTOM_OPTIONS),
  };
}

function normalizeOptionValues(selected: string[], customValues: string[]) {
  const goals = selected.filter((goal) => goal !== OTHER_GOAL_VALUE);

  return [
    ...goals,
    ...customValues.map((value) => value.trim()).filter(Boolean),
  ].slice(0, goals.length + MAX_CUSTOM_OPTIONS);
}

function mergeCustomValues(
  current: string[],
  draft: string,
  options: ChipOption[] = [],
) {
  const optionLabels = new Set(
    options.flatMap((option) => [
      option.label.toLowerCase(),
      option.value.toLowerCase(),
    ]),
  );
  const next = [...current];
  const value = draft.trim();

  if (
    value &&
    !optionLabels.has(value.toLowerCase()) &&
    !next.some((item) => item.toLowerCase() === value.toLowerCase()) &&
    next.length < MAX_CUSTOM_OPTIONS
  ) {
    next.push(value);
  }

  return next.slice(0, MAX_CUSTOM_OPTIONS);
}

function resolveLanguageValue(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  const option = LANGUAGE_OPTIONS.find(
    (language) =>
      language.value !== CUSTOM_LANGUAGE_VALUE &&
      (language.value.toLowerCase() === trimmed.toLowerCase() ||
        language.label.toLowerCase() === trimmed.toLowerCase()),
  );

  return option?.label ?? trimmed;
}

function mergeLanguageDraft(current: string[], draft: string) {
  const value = resolveLanguageValue(draft);

  if (
    !value ||
    value.toLowerCase() === CUSTOM_LANGUAGE_VALUE.toLowerCase() ||
    current.some(
      (language) => language.toLowerCase() === value.toLowerCase(),
    ) ||
    current.length >= MAX_LANGUAGES
  ) {
    return current;
  }

  return [...current, value];
}

function splitFriendshipGoals(value: unknown) {
  const result = splitOptionValues(value, FRIENDSHIP_GOALS);
  return {
    selected: result.customValues.length
      ? [OTHER_GOAL_VALUE]
      : result.selected.slice(0, 1),
    customValues: result.customValues.slice(0, 1),
  };
}

function normalizeFriendshipGoals(selected: string[], customValues: string[]) {
  return normalizeOptionValues(selected, customValues).slice(0, 1);
}

function selectionSummary(
  options: ChipOption[],
  selected: string[],
  fallback: string,
  customValues: string[] = [],
) {
  const labels = [
    ...selected
      .map((value) => options.find((option) => option.value === value)?.label)
      .filter((label): label is string => Boolean(label)),
    ...customValues,
  ];

  if (labels.length === 0) {
    return fallback;
  }

  if (labels.length <= 2) {
    return labels.map(toEditProfileLabel).join(", ");
  }

  return `${labels
    .slice(0, 2)
    .map(toEditProfileLabel)
    .join(", ")} +${labels.length - 2}`;
}

function toEditProfileLabel(value: string) {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized) return normalized;

  const lower = normalized.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`
    .replace(/\bi\b/g, "I")
    // The label is lower-cased wholesale above, so the brand has to be put
    // back by name. It read `.replace(/\bopncue\b/g, "opncue")` — a no-op that
    // looked like it was enforcing something.
    .replace(/\bopncue\b/g, "Opncue");
}

function ageFromDateOfBirth(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const dob = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }

  const now = new Date();
  let nextAge = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - dob.getUTCMonth();

  if (
    monthDelta < 0 ||
    (monthDelta === 0 && now.getUTCDate() < dob.getUTCDate())
  ) {
    nextAge -= 1;
  }

  return nextAge >= 0 ? nextAge : null;
}

function ageBandFromDateOfBirth(value: string): string | null {
  const nextAge = ageFromDateOfBirth(value);
  if (nextAge === null) {
    return null;
  }

  if (nextAge < 25) {
    return "18-24";
  }
  if (nextAge >= 75) {
    return "75+";
  }

  const start = Math.floor(nextAge / 5) * 5;
  return `${start}-${start + 4}`;
}

export default function EditProfile() {
  const { t } = useTranslation();
  const router = useRouter();
  const { colors, isDark, statusBarStyle } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  const user = useAuthStore((state) => state.user);

  const setProfile = useAuthStore((state) => state.setProfile);

  const initialProfile = useMemo(() => {
    return {
      ...(user ?? {}),
    };
  }, [user]);

  const initialLookingFor = splitFriendshipGoals(initialProfile.lookingFor);
  const initialInterests = splitOptionValues(
    [
      ...cleanStringArray(initialProfile.interests),
      ...cleanStringArray(initialProfile.additionalInterests),
    ],
    INTEREST_OPTIONS,
  );

  const initialDisplayName = useMemo(
    () =>
      initialProfile.displayName?.trim() ||
      generateDisplayName(
        initialProfile.id ?? initialProfile.email ?? initialProfile.avatarSeed,
      ),
    [
      initialProfile.avatarSeed,
      initialProfile.displayName,
      initialProfile.email,
      initialProfile.id,
    ],
  );

  const [displayName, setDisplayName] = useState(initialDisplayName);

  // Read-only: date of birth is captured at signup and shown here as an age
  // band. Kept in state only so it passes through the save payload unchanged.
  const [dateOfBirth] = useState(initialProfile.dateOfBirth ?? "");

  const [headline, setHeadline] = useState(initialProfile.headline ?? "");

  const [bio, setBio] = useState(initialProfile.about ?? "");

  const [interests, setInterests] = useState<string[]>(
    initialInterests.selected,
  );
  const [customInterestDraft, setCustomInterestDraft] = useState("");
  const [customInterests, setCustomInterests] = useState(
    initialInterests.customValues,
  );

  const [lookingFor, setLookingFor] = useState<string[]>(
    initialLookingFor.selected,
  );
  const [customLookingForDraft, setCustomLookingForDraft] = useState("");
  const [customLookingFor, setCustomLookingFor] = useState(
    initialLookingFor.customValues,
  );

  const [languages, setLanguages] = useState<string[]>(
    cleanStringArray(initialProfile.languages).filter(
      (language) => language !== CUSTOM_LANGUAGE_VALUE,
    ),
  );
  const [customLanguageOpen, setCustomLanguageOpen] = useState(false);
  const [customLanguageDraft, setCustomLanguageDraft] = useState("");

  const [religion, setReligion] = useState(initialProfile.religion ?? "");

  const [politics, setPolitics] = useState(initialProfile.politics ?? "");

  const [gender, setGender] = useState(initialProfile.gender ?? "");
  // A concrete gender is locked server-side (409 on any change); only null
  // and the two uncommitted responses may still move. The list mirrors the
  // backend's LOCKED_GENDERS in common/gender.ts — the server is the
  // authority, this only decides what the editor offers.
  const genderLocked = ["woman", "man", "non-binary"].includes(
    initialProfile.gender ?? "",
  );

  // The nearby range is NOT part of the deferred save form: it is also
  // editable from the Home filter sheets, so a local copy here could go stale
  // and "Save changes" would silently write an old radius back. The slider
  // reads the store directly and commits through the one shared action.
  const saveNearbyRadius = useSessionStore((state) => state.saveNearbyRadius);

  async function commitNearbyRadius(radiusM: number) {
    try {
      await saveNearbyRadius(radiusM);
    } catch (err) {
      notify(
        t("home.nearbyRange.updateFailedTitle"),
        err instanceof Error ? err.message : t("errors.tryAgainInAMoment"),
      );
    }
  }

  // Audience preferences — who the user is willing to be visible to.
  const [audienceGenders, setAudienceGenders] = useState<string[]>(
    initialProfile.visibilityPreferences?.genders ?? [],
  );

  const [audienceAgeRange, setAudienceAgeRange] = useState<string | null>(
    visibilityRangeToLabel(
      initialProfile.visibilityPreferences?.ageMin,
      initialProfile.visibilityPreferences?.ageMax,
    ),
  );

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // The profile editor screen is a tab, so it stays mounted after saving. Clear the
  // "Saved" state whenever the user comes back so the button reads "Save
  // changes" and is tappable again for further edits.
  useFocusEffect(
    useCallback(() => {
      setSaved(false);
    }, []),
  );

  const [nameError, setNameError] = useState<string>();

  const selectedCount = interests.filter(
    (interest) => interest !== OTHER_GOAL_VALUE,
  ).length;
  const lookingForSummary = selectionSummary(
    LOOKING_FOR_OPTIONS,
    lookingFor,
    t("profile.validation.chooseReason"),
    customLookingFor,
  );
  const interestsSummary = selectionSummary(
    INTEREST_OPTIONS,
    interests,
    t("profile.validation.chooseInterests"),
    customInterests,
  );
  const languageSummary = selectionSummary(
    LANGUAGE_FILTER_OPTIONS,
    languages,
    "Optional",
  );
  const genderSummary = selectionSummary(
    OPTIONAL_OWN_GENDER_OPTIONS,
    gender ? [gender] : [],
    "Optional",
  );
  const audienceGenderSummary = selectionSummary(
    AUDIENCE_GENDER_FILTER_OPTIONS,
    audienceGenders.length ? audienceGenders : [ALL_GENDERS_VALUE],
    "All",
  );
  const religionSummary = selectionSummary(
    OPTIONAL_RELIGION_OPTIONS,
    religion ? [religion] : [],
    "Optional",
  );
  const politicsSummary = selectionSummary(
    OPTIONAL_POLITICS_OPTIONS,
    politics ? [politics] : [],
    t("profile.edit.optional"),
  );
  const ageBandDisplay =
    ageBandFromDateOfBirth(dateOfBirth) ?? initialProfile.ageBand ?? null;

  function closeProfile() {
    // Always return to the profile tab, not wherever `back` would land.
    router.replace("/profile");
  }

  function toggleMultiValue(
    value: string,
    current: string[],
    setCurrent: (next: string[]) => void,
    limit?: number,
  ) {
    if (current.includes(value)) {
      setCurrent(current.filter((item) => item !== value));

      return;
    }

    if (limit !== undefined && current.length >= limit) {
      notify(
        t("profile.edit.interestLimitTitle"),
        t("profile.edit.interestLimitBody", { count: limit }),
      );

      return;
    }

    setCurrent([...current, value]);
  }

  function selectSingleValue(
    value: string,
    current: string,
    setCurrent: (next: string) => void,
  ) {
    if (value === OPTIONAL_VALUE) {
      setCurrent("");
      return;
    }
    setCurrent(current === value ? "" : value);
  }

  function selectLanguage(value: string) {
    if (value === OPTIONAL_VALUE) {
      setLanguages([]);
      setCustomLanguageOpen(false);
      setCustomLanguageDraft("");
      return;
    }

    addLanguage(value);
  }

  function toggleAudienceGender(value: string) {
    if (value === ALL_GENDERS_VALUE) {
      setAudienceGenders([]);
      return;
    }

    toggleMultiValue(value, audienceGenders, setAudienceGenders);
  }

  function addCustomListValue(
    draft: string,
    current: string[],
    setCurrent: (next: string[]) => void,
    setDraft: (next: string) => void,
    label: string,
    options: ChipOption[] = [],
  ) {
    const value = draft.trim();
    const optionValues = new Set(
      options.flatMap((option) => [
        option.label.toLowerCase(),
        option.value.toLowerCase(),
      ]),
    );

    if (!value) {
      notify(
        t("profile.edit.addItemTitle", { label }),
        t("profile.edit.typeItemFirst", { label }),
      );
      return;
    }

    if (optionValues.has(value.toLowerCase())) {
      notify(
        t("profile.edit.alreadyAvailableTitle"),
        t("profile.edit.alreadyAvailableBody", { value }),
      );
      return;
    }

    if (current.some((item) => item.toLowerCase() === value.toLowerCase())) {
      notify(
        t("profile.edit.alreadyAddedTitle"),
        t("profile.edit.alreadyAddedBody", { value }),
      );
      return;
    }

    if (current.length >= MAX_CUSTOM_OPTIONS) {
      notify(
        t("profile.edit.customLimitTitle"),
        t("profile.edit.customLimitBody", { count: MAX_CUSTOM_OPTIONS, label }),
      );
      return;
    }

    setCurrent([...current, value]);
    setDraft("");
  }

  function addCustomFriendshipGoal() {
    const value = customLookingForDraft.trim();
    const optionValues = new Set(
      LOOKING_FOR_OPTIONS.flatMap((option) => [
        option.label.toLowerCase(),
        option.value.toLowerCase(),
      ]),
    );

    if (!value) {
      notify(
        t("profile.edit.addReasonTitle"),
        t("profile.edit.typeReasonFirst"),
      );
      return;
    }

    if (optionValues.has(value.toLowerCase())) {
      notify(
        t("profile.edit.alreadyAvailableTitle"),
        t("profile.edit.alreadyAvailableBody", { value }),
      );
      return;
    }

    setLookingFor([OTHER_GOAL_VALUE]);
    setCustomLookingFor([value]);
    setCustomLookingForDraft("");
  }

  function addLanguage(value: string) {
    if (!value) {
      return;
    }

    if (value === CUSTOM_LANGUAGE_VALUE) {
      setCustomLanguageOpen(true);
      return;
    }

    if (languages.includes(value)) {
      return;
    }

    if (languages.length >= MAX_LANGUAGES) {
      notify(
        t("profile.edit.languageLimitTitle"),
        t("profile.edit.languageLimitBody", { count: MAX_LANGUAGES }),
      );
      return;
    }

    setLanguages([...languages, value]);
  }

  function addCustomLanguage() {
    const value = resolveLanguageValue(customLanguageDraft);

    if (!value) {
      notify(
        t("profile.edit.addLanguageTitle"),
        t("profile.edit.typeLanguageFirst"),
      );
      return;
    }

    if (value.toLowerCase() === CUSTOM_LANGUAGE_VALUE.toLowerCase()) {
      notify(
        t("profile.edit.addLanguageTitle"),
        t("profile.edit.typeLanguageToAdd"),
      );
      return;
    }

    if (
      languages.some(
        (language) => language.toLowerCase() === value.toLowerCase(),
      )
    ) {
      notify(
        t("profile.edit.alreadyAddedTitle"),
        t("profile.edit.languageAlreadyAdded", { value }),
      );
      return;
    }

    if (languages.length >= MAX_LANGUAGES) {
      notify(
        t("profile.edit.languageLimitTitle"),
        t("profile.edit.languageLimitBody", { count: MAX_LANGUAGES }),
      );
      return;
    }

    setLanguages([...languages, value]);
    setCustomLanguageDraft("");
  }

  async function onSave() {
    const trimmedName = collapseDisplayName(displayName);
    const trimmedHeadline = headline.trim();
    const trimmedBio = bio.trim();
    const trimmedDob = dateOfBirth.trim();
    const nextCustomInterests = mergeCustomValues(
      customInterests,
      customInterestDraft,
      INTEREST_OPTIONS,
    );
    const nextCustomLookingFor = customLookingForDraft.trim()
      ? [customLookingForDraft.trim()]
      : customLookingFor.slice(0, 1);
    const normalizedLanguages = mergeLanguageDraft(
      languages,
      customLanguageDraft,
    );
    const normalizedInterests = normalizeOptionValues(
      interests,
      nextCustomInterests,
    );
    const normalizedLookingFor = normalizeFriendshipGoals(
      lookingFor,
      nextCustomLookingFor,
    );
    let valid = true;

    const nameValidationError = validateDisplayName(displayName);
    setNameError(nameValidationError);
    if (nameValidationError) {
      valid = false;
    }

    // Date of birth is set at signup and can't be edited here, so there's
    // nothing to validate — and it is no longer sent at all (write-once
    // server-side; see the payload below).

    if (!valid) {
      return;
    }

    // "50+" persists as ageMax 120 (the match ceiling), matching what the
    // eligibility check on the server means by open-ended.
    const audienceRange = parseAgeRangeForMatch(audienceAgeRange);
    const visibilityPreferences: VisibilityPreferences = {
      ageMin: audienceRange?.min ?? null,
      ageMax: audienceRange?.max ?? null,
      genders: audienceGenders.length > 0 ? audienceGenders : null,
    };

    const nextProfile: ProfileDraft = {
      displayName: trimmedName,
      dateOfBirth: trimmedDob,
      headline: trimmedHeadline,
      bio: trimmedBio,
      interests: normalizedInterests,
      lookingFor: normalizedLookingFor,
      languages: normalizedLanguages,
      religion,
      politics,
      gender,
    };

    setSaving(true);
    setSaved(false);

    try {
      await setProfile({
        displayName: nextProfile.displayName,
        headline: nextProfile.headline || null,
        about: nextProfile.bio || null,
        // Date of birth is write-once on the server and read-only here, so it
        // is not sent at all: resending a cached value after a support
        // correction would 409 the whole save. Locked gender is omitted for
        // the same reason; while it may still move, the chosen value goes up.
        interests: nextProfile.interests,
        additionalInterests: [],
        lookingFor: nextProfile.lookingFor,
        additionalFriendshipGoals: [],
        languages: nextProfile.languages,
        religion: nextProfile.religion || null,
        politics: nextProfile.politics || null,
        ...(genderLocked ? {} : { gender: nextProfile.gender || null }),
        visibilityPreferences,
      });

      setSaved(true);
      setTimeout(() => {
        router.replace("/profile");
      }, 550);
    } catch {
      notify(
        t("profile.edit.saveFailedTitle"),
        t("profile.edit.saveFailedBody"),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <SafeAreaView style={styles.screen} edges={["top", "bottom"]}>
      <StatusBar style={statusBarStyle} />

      <View style={styles.page}>
          <View style={styles.content}>
            <View style={styles.header}>
              <Text style={styles.headerTitle}>{t("profile.edit.title")}</Text>

              <Text style={styles.headerDescription}>
                {t("profile.edit.body")}
              </Text>
            </View>

            {/*
              Same shape as Screen's scroll + footer (see screen.tsx): the
              keyboard is allowed to COVER the pinned footer rather than lift
              it — lifting put Cancel/Save on top of the field being typed
              into. The scroll keeps the focused input visible; an unhandled
              tap outside an input dismisses the keyboard and the footer is
              back.
            */}
            <KeyboardScrollView
              bounces={false}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.form}
            >
              <Pressable
                onPress={() => router.push("/edit-avatar")}
                accessibilityRole="button"
                accessibilityLabel={t("profile.avatar.editA11y")}
                accessibilityHint={t("profile.avatar.editHint")}
                style={styles.avatarRow}
              >
                <Avatar
                  seed={user?.avatarSeed ?? user?.displayName ?? "opncue"}
                  avatarConfig={user?.avatarConfig}
                  name={user?.displayName}
                  size={56}
                />
                <View style={styles.avatarRowCopy}>
                  <Text style={styles.avatarRowTitle}>
                    {t("profile.avatar.title")}
                  </Text>
                  <Text style={styles.avatarRowBody} numberOfLines={2}>
                    {t("profile.avatar.subtitle")}
                  </Text>
                </View>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.iconMuted}
                />
              </Pressable>

              <Section
                icon="person-outline"
                title={t("profile.edit.visibleSection")}
                description={t("profile.edit.visibleSectionBody")}
              >
                <FieldLabel label={t("profile.edit.displayName")} required />

                <View
                  style={[
                    styles.inputContainer,
                    nameError ? styles.inputContainerError : null,
                  ]}
                >
                  <Ionicons
                    name="person-outline"
                    size={19}
                    color={colors.iconMuted}
                  />

                  <TextInput
                    value={displayName}
                    onChangeText={(value) => {
                      setDisplayName(value);

                      if (nameError) {
                        setNameError(undefined);
                      }
                    }}
                    placeholder={t("profile.edit.displayNamePlaceholder")}
                    placeholderTextColor={colors.textMuted}
                    autoCapitalize="words"
                    autoCorrect={false}
                    autoComplete="name"
                    maxLength={MAX_DISPLAY_NAME_LENGTH}
                    returnKeyType="next"
                    selectionColor={colors.primary}
                    style={styles.input}
                  />
                </View>

                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    setDisplayName(generateDisplayName());
                    if (nameError) {
                      setNameError(undefined);
                    }
                  }}
                  style={styles.randomNameButton}
                >
                  <Ionicons
                    name="shuffle-outline"
                    size={14}
                    color={colors.textInverse}
                  />
                  <Text style={styles.addButtonText}>
                    {t("profile.edit.randomName")}
                  </Text>
                </Pressable>

                {nameError ? <ErrorMessage message={nameError} /> : null}

                <FieldLabel label={t("profile.edit.ageBand")} />

                <View style={[styles.inputContainer, styles.readOnlyContainer]}>
                  <Ionicons
                    name="calendar-outline"
                    size={19}
                    color={colors.iconMuted}
                  />

                  <Text style={styles.readOnlyValue}>
                    {ageBandDisplay ?? t("profile.edit.notSet")}
                  </Text>

                  <Ionicons
                    name="lock-closed-outline"
                    size={16}
                    color={colors.iconMuted}
                  />
                </View>

                <FieldLabel
                  label={t("profile.edit.headline")}
                  description={`${headline.length}/${MAX_HEADLINE_LENGTH}`}
                />

                <View style={styles.inputContainer}>
                  <Ionicons
                    name="chatbubble-ellipses-outline"
                    size={19}
                    color={colors.iconMuted}
                  />

                  <TextInput
                    value={headline}
                    onChangeText={setHeadline}
                    placeholder={t("profile.edit.headlinePlaceholder")}
                    placeholderTextColor={colors.textMuted}
                    autoCapitalize="sentences"
                    maxLength={MAX_HEADLINE_LENGTH}
                    selectionColor={colors.primary}
                    style={styles.input}
                  />
                </View>

                <FieldLabel
                  label={t("profile.edit.bio")}
                  description={`${bio.length}/${MAX_BIO_LENGTH}`}
                />

                <View style={styles.bioContainer}>
                  {bio.length === 0 ? (
                    <View
                      pointerEvents="none"
                      style={styles.bioPlaceholderWrap}
                    >
                      <Text style={styles.bioPlaceholder}>
                        {t("profile.edit.bioHelp")}
                      </Text>
                    </View>
                  ) : null}

                  <TextInput
                    value={bio}
                    onChangeText={setBio}
                    autoCapitalize="sentences"
                    multiline
                    maxLength={MAX_BIO_LENGTH}
                    selectionColor={colors.primary}
                    textAlignVertical="top"
                    style={styles.bioInput}
                  />
                </View>

                <FieldLabel label={t("profile.edit.languages")} />

                <FilterSelect
                  label={t("profile.edit.languages")}
                  valueLabel={languageSummary}
                  options={LANGUAGE_FILTER_OPTIONS}
                  selected={languages.length ? languages : [OPTIONAL_VALUE]}
                  multiple
                  compact
                  hint={t("profile.edit.selectedCount", {
                    selected: languages.length,
                    limit: MAX_LANGUAGES,
                  })}
                  onPress={selectLanguage}
                />

                {customLanguageOpen ? (
                  <View style={styles.inputContainer}>
                    <Ionicons
                      name="create-outline"
                      size={19}
                      color={colors.iconMuted}
                    />

                    <TextInput
                      value={customLanguageDraft}
                      onChangeText={setCustomLanguageDraft}
                      placeholder={t("profile.edit.languagePlaceholder")}
                      placeholderTextColor={colors.textMuted}
                      autoCapitalize="words"
                      maxLength={40}
                      selectionColor={colors.primary}
                      style={styles.input}
                    />

                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={t("profile.edit.addLanguageA11y")}
                      onPress={addCustomLanguage}
                      style={styles.addButton}
                    >
                      <Text style={styles.addButtonText}>
                        {t("common.add")}
                      </Text>
                    </Pressable>
                  </View>
                ) : null}

                {languages.length ? (
                  <ChipGroup
                    options={languages.map((language) => ({
                      label: language,
                      value: language,
                    }))}
                    selected={languages}
                    onPress={(value) =>
                      setLanguages(
                        languages.filter((language) => language !== value),
                      )
                    }
                  />
                ) : null}
              </Section>

              <View style={styles.filterFieldsPanel}>
                <FilterField
                  label={t("profile.edit.friendshipGoal")}
                  description={t("profile.edit.friendshipGoalBody")}
                >
                  <FilterSelect
                    label={t("profile.edit.friendshipGoal")}
                    valueLabel={lookingForSummary}
                    options={LOOKING_FOR_OPTIONS}
                    selected={lookingFor}
                    stayOpenOnValues={[OTHER_GOAL_VALUE]}
                    onPress={(value) => {
                      const active = lookingFor.includes(value);
                      setLookingFor(active ? [] : [value]);
                      if (value !== OTHER_GOAL_VALUE) {
                        setCustomLookingFor([]);
                        setCustomLookingForDraft("");
                      }
                    }}
                  >
                    {lookingFor.includes(OTHER_GOAL_VALUE) ? (
                      <View style={styles.inputContainer}>
                        <Ionicons
                          name="create-outline"
                          size={19}
                          color={colors.iconMuted}
                        />

                        <TextInput
                          value={customLookingForDraft}
                          onChangeText={setCustomLookingForDraft}
                          placeholder={t("profile.edit.goalPlaceholder")}
                          placeholderTextColor={colors.textMuted}
                          autoCapitalize="sentences"
                          maxLength={40}
                          selectionColor={colors.primary}
                          style={styles.input}
                        />

                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={t("profile.edit.addGoalA11y")}
                          onPress={addCustomFriendshipGoal}
                          style={styles.addButton}
                        >
                          <Text style={styles.addButtonText}>
                            {t("common.add")}
                          </Text>
                        </Pressable>
                      </View>
                    ) : null}

                    {customLookingFor.length ? (
                      <ChipGroup
                        options={customLookingFor.map((value) => ({
                          label: value,
                          value,
                        }))}
                        selected={customLookingFor}
                        onPress={(value) =>
                          setCustomLookingFor(
                            customLookingFor.filter((item) => item !== value),
                          )
                        }
                      />
                    ) : null}
                  </FilterSelect>
                </FilterField>

                <FilterField
                  label={t("profile.edit.interests")}
                  description={t("profile.edit.interestHint", {
                    count: MAX_INTERESTS,
                  })}
                >
                  <FilterSelect
                    label={t("profile.edit.interests")}
                    valueLabel={interestsSummary}
                    options={INTEREST_OPTIONS}
                    selected={interests}
                    multiple
                    hint={`${t("profile.edit.selectedCount", {
                      selected: selectedCount,
                      limit: MAX_INTERESTS,
                    })}${
                      customInterests.length
                        ? ` · ${t("profile.edit.customSelected", {
                            selected: customInterests.length,
                            limit: MAX_CUSTOM_OPTIONS,
                          })}`
                        : ""
                    }`}
                    onPress={(value) =>
                      toggleMultiValue(
                        value,
                        interests,
                        setInterests,
                        MAX_INTERESTS,
                      )
                    }
                  >
                    {interests.includes(OTHER_GOAL_VALUE) ? (
                      <View style={styles.inputContainer}>
                        <Ionicons
                          name="create-outline"
                          size={19}
                          color={colors.iconMuted}
                        />

                        <TextInput
                          value={customInterestDraft}
                          onChangeText={setCustomInterestDraft}
                          placeholder={t("profile.edit.interestPlaceholder")}
                          placeholderTextColor={colors.textMuted}
                          autoCapitalize="sentences"
                          maxLength={40}
                          selectionColor={colors.primary}
                          style={styles.input}
                        />

                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={t("profile.edit.addInterestA11y")}
                          onPress={() =>
                            addCustomListValue(
                              customInterestDraft,
                              customInterests,
                              setCustomInterests,
                              setCustomInterestDraft,
                              t("profile.edit.interestLabel"),
                              INTEREST_OPTIONS,
                            )
                          }
                          style={styles.addButton}
                        >
                          <Text style={styles.addButtonText}>
                            {t("common.add")}
                          </Text>
                        </Pressable>
                      </View>
                    ) : null}

                    {customInterests.length ? (
                      <ChipGroup
                        options={customInterests.map((value) => ({
                          label: value,
                          value,
                        }))}
                        selected={customInterests}
                        onPress={(value) =>
                          setCustomInterests(
                            customInterests.filter((item) => item !== value),
                          )
                        }
                      />
                    ) : null}
                  </FilterSelect>
                </FilterField>

                <FilterField
                  label={t("profile.edit.gender")}
                  description={
                    genderLocked ? t("profile.edit.genderLocked") : undefined
                  }
                >
                  {genderLocked ? (
                    <View style={styles.filterSelectHeader}>
                      <View style={styles.filterSelectCopy}>
                        <Text numberOfLines={1} style={styles.filterSelectValue}>
                          {genderSummary}
                        </Text>
                      </View>
                      <Ionicons
                        name="lock-closed"
                        size={16}
                        color={colors.iconMuted}
                      />
                    </View>
                  ) : (
                    <FilterSelect
                      label={t("profile.edit.gender")}
                      valueLabel={genderSummary}
                      options={OPTIONAL_OWN_GENDER_OPTIONS}
                      selected={gender ? [gender] : [OPTIONAL_VALUE]}
                      onPress={(value) =>
                        selectSingleValue(value, gender, setGender)
                      }
                    />
                  )}
                </FilterField>

                <FilterField
                  label={t("profile.edit.ageRange")}
                  description={t("profile.edit.ageRangeBody")}
                >
                  <AgeRangeSlider
                    selected={audienceAgeRange}
                    onChange={setAudienceAgeRange}
                    colors={{
                      track: colors.primary,
                      trackRemaining: colors.border,
                      thumb: colors.primary,
                      label: colors.text,
                      labelMuted: colors.textMuted,
                    }}
                  />
                </FilterField>

                <FilterField
                  label={t("profile.edit.genderPreference")}
                  description={t("profile.edit.genderPreferenceBody")}
                >
                  <FilterSelect
                    label={t("profile.edit.genderPreference")}
                    valueLabel={audienceGenderSummary}
                    options={AUDIENCE_GENDER_FILTER_OPTIONS}
                    selected={
                      audienceGenders.length
                        ? audienceGenders
                        : [ALL_GENDERS_VALUE]
                    }
                    multiple
                    onPress={toggleAudienceGender}
                  />
                </FilterField>

                <FilterField
                  label={t("profile.edit.nearbyRange")}
                  description={t("profile.edit.nearbyRangeBody")}
                >
                  <NearbyRadiusSlider
                    valueM={clampNearbyRadiusM(user?.nearbyRadiusM)}
                    onCommit={commitNearbyRadius}
                    colors={{
                      track: colors.primary,
                      trackRemaining: colors.border,
                      thumb: colors.primary,
                      label: colors.text,
                      labelMuted: colors.textMuted,
                    }}
                  />
                </FilterField>

                <FilterField label={t("profile.edit.religion")}>
                  <FilterSelect
                    label={t("profile.edit.religion")}
                    valueLabel={religionSummary}
                    options={OPTIONAL_RELIGION_OPTIONS}
                    selected={religion ? [religion] : [OPTIONAL_VALUE]}
                    onPress={(value) =>
                      selectSingleValue(value, religion, setReligion)
                    }
                  />
                </FilterField>

                <FilterField label={t("profile.edit.politics")} last>
                  <FilterSelect
                    label={t("profile.edit.politics")}
                    valueLabel={politicsSummary}
                    options={OPTIONAL_POLITICS_OPTIONS}
                    selected={politics ? [politics] : [OPTIONAL_VALUE]}
                    onPress={(value) =>
                      selectSingleValue(value, politics, setPolitics)
                    }
                  />
                </FilterField>
              </View>
            </KeyboardScrollView>

            <View style={styles.footer}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("profile.edit.cancelA11y")}
                disabled={saving}
                onPress={closeProfile}
                style={({ pressed }) => [
                  styles.cancelButton,
                  pressed && !saving && styles.cancelButtonPressed,
                  saving && styles.buttonDisabled,
                ]}
              >
                <Ionicons name="close" size={19} color={colors.textBody} />

                <Text style={styles.cancelButtonText}>
                  {t("common.cancel")}
                </Text>
              </Pressable>

              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("profile.edit.saveA11y")}
                disabled={saving || saved}
                onPress={() => void onSave()}
                style={({ pressed }) => [
                  styles.saveButton,
                  pressed && !saving && !saved && styles.saveButtonPressed,
                  saving && styles.buttonDisabled,
                ]}
              >
                {saving ? (
                  <ActivityIndicator color={colors.textInverse} />
                ) : (
                  <>
                    <Ionicons
                      name={saved ? "checkmark-circle" : "checkmark"}
                      size={20}
                      color={colors.textInverse}
                    />

                    <Text style={styles.saveButtonText}>
                      {saved
                        ? t("profile.edit.saved")
                        : t("profile.edit.save")}
                    </Text>
                  </>
                )}
              </Pressable>
            </View>
          </View>
      </View>
    </SafeAreaView>
  );
}

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionIcon}>
          <Ionicons name={icon} size={20} color={colors.primary} />
        </View>

        <View style={styles.sectionHeaderCopy}>
          <Text style={styles.sectionTitle}>{title}</Text>

          <Text style={styles.sectionDescription}>{description}</Text>
        </View>
      </View>

      <View style={styles.sectionContent}>{children}</View>
    </View>
  );
}

function FieldLabel({
  label,
  description,
  required = false,
}: {
  label: string;
  description?: string;
  required?: boolean;
}) {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  return (
    <View style={styles.fieldLabelRow}>
      <Text style={styles.fieldLabel}>
        {label}

        {required ? <Text style={styles.requiredMark}> *</Text> : null}
      </Text>

      {description ? (
        <Text style={styles.fieldDescription}>{description}</Text>
      ) : null}
    </View>
  );
}

function ErrorMessage({ message }: { message: string }) {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  return (
    <View style={styles.errorRow}>
      <Ionicons
        name="alert-circle-outline"
        size={15}
        color={colors.dangerStrong}
      />

      <Text style={styles.errorText}>{message}</Text>
    </View>
  );
}

function FilterField({
  label,
  description,
  children,
  last = false,
}: {
  label: string;
  description?: string;
  children: ReactNode;
  last?: boolean;
}) {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  return (
    <View style={[styles.filterField, last && styles.filterFieldLast]}>
      <Text style={styles.filterFieldLabel}>{label}</Text>

      {description ? (
        <Text style={styles.filterFieldDescription}>{description}</Text>
      ) : null}

      {children}
    </View>
  );
}

function FilterSelect({
  label,
  valueLabel,
  options,
  selected,
  onPress,
  multiple = false,
  compact = false,
  hint,
  children,
  stayOpenOnValues = [],
}: {
  label: string;
  valueLabel: string;
  options: ChipOption[];
  selected: string[];
  onPress: (value: string) => void;
  multiple?: boolean;
  compact?: boolean;
  hint?: string;
  children?: ReactNode;
  stayOpenOnValues?: string[];
}) {
  const { t } = useTranslation();
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const [open, setOpen] = useState(false);

  function choose(value: string) {
    onPress(value);
    if (!multiple && !stayOpenOnValues.includes(value)) {
      setOpen(false);
    }
  }

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("profile.edit.fieldValueA11y", {
          label,
          value: valueLabel,
        })}
        accessibilityState={{ expanded: open }}
        onPress={() => setOpen(true)}
        style={({ pressed }) => [
          styles.filterSelectHeader,
          compact && styles.filterSelectHeaderCompact,
          pressed && styles.filterSelectHeaderPressed,
        ]}
      >
        <View style={styles.filterSelectCopy}>
          <Text
            numberOfLines={1}
            style={[
              styles.filterSelectValue,
              compact && styles.filterSelectValueCompact,
            ]}
          >
            {valueLabel}
          </Text>
        </View>

        <Ionicons name="chevron-down" size={18} color={colors.iconMuted} />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <BlurredBackdrop
          style={styles.filterSelectBackdrop}
          accessibilityLabel={t("profile.edit.closeOptionsA11y", { label })}
          onPress={() => setOpen(false)}
        />

        <View style={styles.filterSelectCenter} pointerEvents="box-none">
          <View style={styles.filterSelectSheet}>
            <View style={styles.filterSelectSheetHeader}>
              <Text style={styles.filterSelectSheetTitle}>{label}</Text>

              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("profile.edit.closeOptionsA11y", {
                  label,
                })}
                hitSlop={10}
                onPress={() => setOpen(false)}
              >
                <Ionicons name="close" size={21} color={colors.iconMuted} />
              </Pressable>
            </View>

            {hint ? <Text style={styles.filterSelectHint}>{hint}</Text> : null}

            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.filterSelectOptions}
            >
              {options.map((option) => {
                const active = selected.includes(option.value);

                return (
                  <FilterSelectOption
                    key={option.value}
                    label={option.label}
                    description={option.description}
                    active={active}
                    multiple={multiple}
                    compact={compact}
                    onPress={() => choose(option.value)}
                  />
                );
              })}
            </ScrollView>

            {children ? (
              <View style={styles.filterSelectChildren}>{children}</View>
            ) : null}

            {multiple ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => setOpen(false)}
                style={({ pressed }) => [
                  styles.filterSelectDone,
                  pressed && styles.filterSelectDonePressed,
                ]}
              >
                <Text style={styles.filterSelectDoneText}>
                  {t("common.done")}
                </Text>
              </Pressable>
            ) : null}
          </View>
        </View>
      </Modal>
    </>
  );
}

function FilterSelectOption({
  label,
  description,
  active,
  multiple = false,
  compact = false,
  onPress,
}: {
  label: string;
  description?: string;
  active: boolean;
  multiple?: boolean;
  compact?: boolean;
  onPress: () => void;
}) {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const displayLabel = toEditProfileLabel(label);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      // One phrase, so the reason is announced with the option rather than
      // trailing after it as a separate element.
      accessibilityLabel={
        description ? `${displayLabel}. ${description}` : displayLabel
      }
      onPress={onPress}
      style={({ pressed }) => [
        styles.filterSelectOption,
        compact && styles.filterSelectOptionCompact,
        description != null && styles.filterSelectOptionStacked,
        pressed && styles.filterSelectOptionPressed,
      ]}
    >
      {multiple ? (
        <Ionicons
          name={active ? "checkbox" : "square-outline"}
          size={21}
          color={active ? colors.primary : colors.iconMuted}
        />
      ) : null}

      <View style={styles.filterSelectOptionCopy}>
        <Text
          style={[
            styles.filterSelectOptionText,
            compact && styles.filterSelectOptionTextCompact,
            active && styles.filterSelectOptionTextActive,
          ]}
        >
          {displayLabel}
        </Text>
        {description ? (
          <Text style={styles.filterSelectOptionDescription}>
            {description}
          </Text>
        ) : null}
      </View>

      {active && !multiple ? (
        <Ionicons name="checkmark" size={18} color={colors.primary} />
      ) : null}
    </Pressable>
  );
}

function ChipGroup({
  options,
  selected,
  onPress,
  selectedTone = "primary",
}: {
  options: ChipOption[];
  selected: string[];
  onPress: (value: string) => void;
  selectedTone?: "primary" | "readable";
}) {
  const { colors, isDark } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const readableSelected = selectedTone === "readable";

  return (
    <View style={styles.chipGroup}>
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        const displayLabel = toEditProfileLabel(option.label);

        return (
          <Pressable
            key={option.value}
            accessibilityRole="button"
            accessibilityState={{
              selected: isSelected,
            }}
            accessibilityLabel={displayLabel}
            onPress={() => onPress(option.value)}
            style={({ pressed }) => [
              styles.chip,
              isSelected &&
                (readableSelected
                  ? styles.chipSelectedReadable
                  : styles.chipSelected),
              pressed && styles.chipPressed,
            ]}
          >
            {isSelected ? (
              <Ionicons
                name="checkmark"
                size={14}
                color={
                  readableSelected ? colors.textStrong : colors.textInverse
                }
              />
            ) : null}

            <Text
              style={[
                styles.chipText,
                isSelected &&
                  (readableSelected
                    ? styles.chipTextSelectedReadable
                    : styles.chipTextSelected),
              ]}
            >
              {displayLabel}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function createStyles(colors: AppColors, isDark: boolean) {
  return StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: colors.background,
    },

    page: {
      flex: 1,
      backgroundColor: colors.background,
    },

    content: {
      flex: 1,
      width: "100%",
      overflow: "hidden",
      backgroundColor: colors.background,
    },

    header: {
      minHeight: 76,
      paddingHorizontal: 18,
      paddingVertical: 13,
      alignItems: "center",
      justifyContent: "center",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: colors.surface,
    },

    headerTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 19,
      lineHeight: 24,
      fontWeight: "800",
      letterSpacing: -0.3,
      textAlign: "center",
    },

    headerDescription: {
      maxWidth: 360,
      marginTop: 2,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 16,
      textAlign: "center",
    },

    form: {
      width: "100%",
      maxWidth: 570,
      alignSelf: "center",
      paddingHorizontal: 18,
      paddingTop: 17,
      paddingBottom: 30,
      gap: 16,
    },

    avatarRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      padding: 14,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    avatarRowCopy: { flex: 1, gap: 2 },
    avatarRowTitle: { fontSize: 15, fontWeight: "700", color: colors.text },
    avatarRowBody: { fontSize: 13, color: colors.textSecondary, lineHeight: 18 },

    section: {
      padding: 17,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.16 : 0.06,
      shadowRadius: 12,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 2,
    },

    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
      paddingBottom: 14,
      marginBottom: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
    },

    sectionIcon: {
      width: 39,
      height: 39,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 13,
      backgroundColor: colors.primarySoft,
    },

    sectionHeaderCopy: {
      flex: 1,
    },

    sectionTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 15.5,
      lineHeight: 20,
      fontWeight: "800",
    },

    sectionDescription: {
      marginTop: 2,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 16,
    },

    sectionContent: {
      gap: 10,
    },

    fieldLabelRow: {
      marginTop: 3,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },

    fieldLabel: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 12.5,
      lineHeight: 17,
      fontWeight: "700",
    },

    requiredMark: {
      color: colors.dangerStrong,
    },

    fieldDescription: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 15,
    },

    inputContainer: {
      minHeight: 50,
      paddingHorizontal: 14,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: 13,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    inputContainerError: {
      borderColor: colors.dangerBorder,
      backgroundColor: colors.dangerSurface,
    },

    input: {
      flex: 1,
      height: 50,
      paddingVertical: 0,
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 14.5,
    },

    readOnlyContainer: {
      backgroundColor: colors.surface,
    },

    readOnlyValue: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14.5,
    },

    bioPlaceholderWrap: {
      position: "absolute",
      top: 12,
      left: 14,
      right: 14,
    },

    bioPlaceholder: {
      color: colors.textMuted,
      opacity: 0.6,
      fontFamily: FONT_FAMILY,
      fontSize: 12.5,
      lineHeight: 18,
    },

    addButton: {
      minHeight: 34,
      paddingHorizontal: 12,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 10,
      backgroundColor: colors.primary,
    },

    addButtonText: {
      color: colors.textInverse,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "800",
    },

    randomNameButton: {
      minHeight: 34,
      alignSelf: "flex-start",
      marginTop: 8,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      borderRadius: 10,
      backgroundColor: colors.primary,
    },

    avatarPreview: {
      minHeight: 74,
      paddingHorizontal: 14,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      borderRadius: 13,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    avatarPreviewText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 17,
    },

    bioContainer: {
      minHeight: 106,
      paddingHorizontal: 14,
      paddingVertical: 12,
      borderRadius: 13,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    bioInput: {
      minHeight: 80,
      padding: 0,
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 20,
    },

    errorRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
    },

    errorText: {
      flex: 1,
      color: colors.dangerStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 16,
    },

    chipGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
    },

    chip: {
      minHeight: 35,
      paddingHorizontal: 11,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    chipSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },

    chipSelectedReadable: {
      borderColor: colors.textStrong,
      backgroundColor: colors.surface,
    },

    chipPressed: {
      opacity: 0.76,
      transform: [{ scale: 0.98 }],
    },

    chipText: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "600",
    },

    chipTextSelected: {
      color: colors.textInverse,
    },

    chipTextSelectedReadable: {
      color: colors.textStrong,
    },

    filterFieldsPanel: {
      padding: 17,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.16 : 0.06,
      shadowRadius: 12,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 2,
    },

    filterField: {
      gap: 8,
      paddingBottom: 12,
      marginBottom: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
    },

    filterFieldLast: {
      paddingBottom: 0,
      marginBottom: 0,
      borderBottomWidth: 0,
    },

    filterFieldLabel: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 12.5,
      lineHeight: 17,
      fontWeight: "800",
    },

    filterFieldDescription: {
      marginTop: -5,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 16,
    },

    filterSelectHeader: {
      minHeight: 44,
      paddingHorizontal: 14,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      borderRadius: 11,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    filterSelectHeaderCompact: {
      minHeight: 40,
      paddingHorizontal: 12,
    },

    filterSelectHeaderPressed: {
      backgroundColor: colors.surfacePressed,
    },

    filterSelectCopy: {
      flex: 1,
      minWidth: 0,
      justifyContent: "center",
    },

    filterSelectValue: {
      color: colors.text,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "600",
    },

    filterSelectValueCompact: {
      fontSize: 12.5,
      lineHeight: 17,
    },

    filterSelectBackdrop: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: "rgba(8, 15, 26, 0.5)",
    },

    filterSelectCenter: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 28,
    },

    filterSelectSheet: {
      width: "100%",
      maxWidth: 380,
      maxHeight: "72%",
      padding: 14,
      gap: 10,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.32 : 0.18,
      shadowRadius: 22,
      shadowOffset: {
        width: 0,
        height: 12,
      },
      elevation: 12,
    },

    filterSelectSheetHeader: {
      minHeight: 32,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },

    filterSelectSheetTitle: {
      flex: 1,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 20,
      fontWeight: "800",
    },

    filterSelectHint: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 16,
      fontWeight: "700",
    },

    filterSelectOptions: {
      gap: 2,
    },

    filterSelectOption: {
      minHeight: 48,
      paddingHorizontal: 14,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      borderRadius: 10,
      backgroundColor: "transparent",
    },

    filterSelectOptionCompact: {
      minHeight: 44,
      paddingHorizontal: 12,
    },

    filterSelectOptionPressed: {
      backgroundColor: colors.surfacePressed,
    },

    filterSelectOptionStacked: {
      paddingVertical: 10,
    },

    filterSelectOptionCopy: {
      flex: 1,
      gap: 2,
    },

    filterSelectOptionText: {
      flex: 1,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 19,
      fontWeight: "700",
    },

    filterSelectOptionDescription: {
      color: colors.textMuted,
      fontFamily: FONT_FAMILY,
      fontSize: 12.5,
      lineHeight: 17,
      fontWeight: "500",
    },

    filterSelectOptionTextCompact: {
      fontSize: 12.5,
      lineHeight: 17,
    },

    filterSelectOptionTextActive: {
      color: colors.primary,
    },

    filterSelectChildren: {
      gap: 10,
    },

    filterSelectDone: {
      minHeight: 42,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 13,
      backgroundColor: colors.primary,
    },

    filterSelectDonePressed: {
      opacity: 0.84,
      transform: [{ scale: 0.99 }],
    },

    filterSelectDoneText: {
      color: colors.textInverse,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "800",
    },

    fieldSpacing: {
      height: 4,
    },

    privacyNotice: {
      padding: 15,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 11,
      borderRadius: 17,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    privacyIcon: {
      width: 37,
      height: 37,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      backgroundColor: colors.primarySoft,
    },

    privacyCopy: {
      flex: 1,
    },

    privacyTitle: {
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: "800",
    },

    privacyDescription: {
      marginTop: 3,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11.5,
      lineHeight: 17,
    },

    footer: {
      paddingHorizontal: 18,
      paddingTop: 9,
      paddingBottom: Platform.OS === "ios" ? 0 : 8,
      flexDirection: "row",
      gap: 11,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.divider,
      backgroundColor: colors.surface,
    },

    cancelButton: {
      flex: 1,
      minWidth: 0,
      minHeight: 50,
      paddingHorizontal: 19,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    cancelButtonPressed: {
      backgroundColor: colors.surfacePressed,
      transform: [{ scale: 0.99 }],
    },

    cancelButtonText: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: "700",
    },

    saveButton: {
      flex: 1,
      minWidth: 0,
      minHeight: 50,
      paddingHorizontal: 18,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      borderRadius: 14,
      backgroundColor: colors.primary,
      shadowColor: colors.primaryStrong,
      shadowOpacity: 0.2,
      shadowRadius: 10,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 4,
    },

    saveButtonPressed: {
      opacity: 0.84,
      transform: [{ scale: 0.99 }],
    },

    saveButtonText: {
      color: colors.textInverse,
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      fontWeight: "800",
    },

    buttonDisabled: {
      opacity: 0.58,
    },
  });
}
